import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import { CardTile, copyLink, slotOrder, formOf } from './ClashDecks';
import { GAMES, postDay } from './News';
import AdSlot from '../components/AdSlot';

/* Text from the publisher may hold internal links as [label](/path), the same
   markup the server renders in prerender.py. */
const LINK = /\[([^\]]+)\]\((\/(?!\/)[^)\s]*)\)/g; // site paths only, never //host
function Rich({ text }) {
  const s = String(text ?? '');
  const out = [];
  let at = 0;
  for (const m of s.matchAll(LINK)) {
    out.push(s.slice(at, m.index));
    out.push(<Link key={m.index} to={m[2]}>{m[1]}</Link>);
    at = m.index + m[0].length;
  }
  out.push(s.slice(at));
  return <>{out}</>;
}

function Block({ b }) {
  if (b.t === 'h2') return <h2 className="section-title" style={{ marginTop: 26 }}>{b.text}</h2>;
  if (b.t === 'p') return <p className="news-p"><Rich text={b.text} /></p>;
  if (b.t === 'table') {
    return (
      <div className="tablecard" style={{ margin: '12px 0' }}>
        <div className="table-scroll">
          <table className="meta">
            <thead><tr>{b.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}><Rich text={c} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (b.t === 'deck') {
    const order = slotOrder(b);
    return (
      <div className="dk" style={{ margin: '12px 0', maxWidth: 520 }}>
        <div className="dk-grid">
          {order.map((c) => <CardTile key={c} id={c} card={b.meta[String(c)]} form={formOf(b, c)} />)}
        </div>
        <div className="dk-foot">
          <span className="pt">{b.name}</span>
          <a className="dk-copy" href={copyLink(order)} target="_blank" rel="noopener noreferrer">Copy to game ↗</a>
        </div>
      </div>
    );
  }
  return null;
}

function NewsPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  usePageMeta({ title: post?.title, description: post?.description });

  const load = useCallback(async () => {
    setPost(null);
    setError(null);
    try {
      setPost(await api.getPost(slug));
    } catch (err) {
      setError(err.message);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page">
      <div className="wrap news-wrap">
        {error ? (
          <ErrorMessage message={error} onRetry={load} />
        ) : !post ? (
          <SkeletonRows rows={8} />
        ) : (
          <article>
            <div className="phead">
              <div>
                <div className="eyebrow">{GAMES[post.game]} · {postDay(post.published_at)}</div>
                <h1>{post.title}</h1>
              </div>
            </div>
            {post.body.map((b, i) => <Block key={i} b={b} />)}
            <AdSlot />
            <p className="news-p" style={{ marginTop: 26 }}><Link to="/news">← More news</Link></p>
          </article>
        )}
      </div>
    </div>
  );
}

export default NewsPost;
